import { useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from 'react-oidc-context';
import { useI18n } from '../i18n';
import { useSyndicate, type DemoPersona } from '../session';
import { AppFooter } from './AppFooter';
import { Logo } from './Logo';

export function Layout() {
  const { t, locale, setLocale } = useI18n();
  const auth = useAuth();
  const syndicate = useSyndicate();
  const base = syndicate.base;
  // The "?" opens the guide at the current page's topics (first path segment after the syndicate).
  const section = useLocation().pathname.slice(base.length + 1).split('/')[0] || 'home';

  return (
    <div className="shell">
      <header className="topbar">
        <div className="brand">
          <Link to="/" className="brand-logo" aria-label={t.console.back}>
            <Logo />
            {syndicate.access === 'demo' && <span className="demo-tag">{t.console.demoTag}</span>}
            {syndicate.access === 'onboarding' && <span className="demo-tag onboarding-tag">{t.console.onboardingTag}</span>}
          </Link>
          {syndicate.access === 'demo' && <PersonaSelect />}
          <div className="brand-syndicate">
            <strong>{syndicate.name}</strong>
            <span className="muted">
              {t.roleLabel(syndicate.roles.length)}
              {syndicate.roles.map((r) => t.roles[r]).join(', ')}
            </span>
          </div>
        </div>
        <div className="topbar-actions">
          {/* Phones get the short labels, so everything fits next to the logo. */}
          <Link to={`${base}/help${section === 'help' ? '' : `?page=${section}`}`} className="link-button" aria-label={t.help.title}>
            <span className="label-long">{t.help.title}</span>
            <span className="label-short help-short">{t.help.short}</span>
          </Link>
          <button type="button" className="link-button" aria-label={t.language} onClick={() => setLocale(locale === 'fr' ? 'en' : 'fr')}>
            <span className="label-long">{t.language}</span>
            <span className="label-short">{t.languageShort}</span>
          </button>
          <button
            type="button"
            className="link-button"
            onClick={() => void auth.signoutRedirect({ id_token_hint: auth.user?.id_token })}
          >
            {t.signOut}
          </button>
        </div>
      </header>
      {syndicate.access === 'support' && (
        <div className="staff-banner staff-banner-support" role="status">
          {t.console.bannerSupport(syndicate.expiresAt ? new Date(syndicate.expiresAt).toLocaleString(locale === 'fr' ? 'fr-CA' : 'en-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '')}{' '}
          <Link to="/">{t.console.back}</Link>
        </div>
      )}
      <nav className="tabs" aria-label="Sections">
        <NavLink to={base} end>
          {t.nav.home}
        </NavLink>
        {syndicate.can('requests.create') && <NavLink to={`${base}/requests`}>{t.nav.requests}</NavLink>}
        {syndicate.can('documents.read') && <NavLink to={`${base}/documents`}>{t.nav.documents}</NavLink>}
        <NavLink to={`${base}/units`}>{t.nav.units}</NavLink>
        {syndicate.can('registry.contacts.read') && <NavLink to={`${base}/parties`}>{t.nav.parties}</NavLink>}
        <NavLink to={`${base}/quote-parts`}>{t.nav.quoteParts}</NavLink>
        {syndicate.can('ledger.read') && <NavLink to={`${base}/finances`}>{t.nav.finances}</NavLink>}
        {syndicate.can('maintenance.read') && <NavLink to={`${base}/maintenance`}>{t.nav.maintenance}</NavLink>}
        {(syndicate.can('governance.read') || syndicate.can('governance.view')) && <NavLink to={`${base}/governance`}>{t.nav.governance}</NavLink>}
        {(syndicate.can('reservations.book') || syndicate.can('reservations.manage')) && <NavLink to={`${base}/reservations`}>{t.nav.reservations}</NavLink>}
        {syndicate.can('insurance.manage') && <NavLink to={`${base}/insurance`}>{t.nav.insurance}</NavLink>}
        {syndicate.can('automation.view') && <NavLink to={`${base}/automation`}>{t.nav.automation}</NavLink>}
        {syndicate.can('onboarding.manage') && <NavLink to={`${base}/onboarding`}>{t.nav.onboarding}</NavLink>}
        {syndicate.can('registry.configure') && <NavLink to={`${base}/settings`}>{t.nav.settings}</NavLink>}
        <NavLink to={`${base}/profile`}>{t.nav.profile}</NavLink>
      </nav>
      <main className="content">
        <Outlet />
      </main>
      <AppFooter association={syndicate.legalStructure === 'npo_with_servitudes'} />
    </div>
  );
}

/** Demo only: see the client as its owner, occupant or manager (fictional accounts), or as a director (yourself). */
function PersonaSelect() {
  const { t } = useI18n();
  const syndicate = useSyndicate();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return (
    <label className="persona-select">
      <span className="muted small">{t.console.viewAs}</span>
      <select
        value={syndicate.persona ?? ''}
        onChange={(e) => {
          syndicate.setPersona((e.target.value || null) as DemoPersona | null);
          queryClient.removeQueries({ queryKey: [syndicate.tenantId] });
          navigate(syndicate.base);
        }}
      >
        <option value="">{t.console.personas.director}</option>
        <option value="manager">{t.console.personas.manager}</option>
        <option value="owner">{t.console.personas.owner}</option>
        <option value="occupant">{t.console.personas.occupant}</option>
      </select>
    </label>
  );
}
